import { API_ORIGIN } from '../config';
const API_BASE_URL = `${API_ORIGIN}/api/employee`;

class EmployeeService {
  async getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }

  // Get all available roles
  async getAllRoles() {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/roles/list`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Get all users with their roles
  async getAllUsers() {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/users/all`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Get specific user's role
  async getUserRole(userId) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/user/${userId}/role`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Update user's role
  async updateUserRole(userId, role) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/user/${userId}/role`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ role }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Get user's additional roles
  async getAdditionalRoles(userId) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/user/${userId}/additional-roles`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      // Normalise: backend may return { additionalRoles: [...] } or { roles: [...] } or a plain array
      // Each item may be a role ID (number) or a role object { role: 2, name: "..." }
      let rawRoles = [];
      if (Array.isArray(data)) {
        rawRoles = data;
      } else if (Array.isArray(data.additionalRoles)) {
        rawRoles = data.additionalRoles;
      } else if (Array.isArray(data.roles)) {
        rawRoles = data.roles;
      }
      // Extract numeric role IDs regardless of whether items are numbers or objects
      const additionalRoles = rawRoles.map(r => (typeof r === 'object' && r !== null ? r.role : r));
      return { additionalRoles };
    } catch (error) {
      throw error;
    }
  }

  // Add an additional role to a user
  async addAdditionalRole(userId, role) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/user/${userId}/additional-roles`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ role }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  // Remove an additional role from a user
  async removeAdditionalRole(userId, role) {
    try {
      const headers = await this.getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/user/${userId}/additional-roles/${role}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      throw error;
    }
  }
}

export default new EmployeeService();
